import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Chip } from "../../chips";
import { readShared } from "../../filters";
import { ItemLink, itemLinkSearch, OptionLink, TabLink } from "../../links";
import { TabPage, useTabQuery } from "../../PageFrame";
import { tabFor } from "../../routes";
import type { PageProps } from "../index";
import { dateGB, Footnote, money, Note, PagedTable, PageHead, usePage } from "../kit";
import { pageSlice, type StalledData } from "../stalled/Page";

export interface MarginRow {
  funnel: string;
  item_id: string;
  item_name: string;
  product_lines: string | null;
  rank: number | null;
  annual_quantity: number | null;
  unit_basis: string | null;
  unit_label: string | null;
  current_unit_cost_gbp: number | null;
  current_cost_source: "manual" | "latest_purchase" | null;
  best_option_id: string | null;
  best_option_name: string | null;
  best_quote_gbp: number | null;
  best_quote_age_days: number | null;
  best_quote_expired: boolean;
  other_basis_n: number;
  saving_per_unit_gbp: number | null;
  at_stake_gbp: number | null;
  no_current_cost: boolean;
  costed_n: number;
  scored_n: number;
  sampled_n: number;
  expired_n: number;
  last_moved_days: number | null;
}

export interface MarginData {
  since: string;
  total_at_stake_gbp: number;
  expired_quotes_n: number;
  ranked_n: number;
  ranked: MarginRow[];
  not_ranked: Array<{ item_id: string; item_name: string }>;
}

export const MARGIN_SUB = "Margin items ranked by £ a year saved if the best costed option is chosen · click a row to open the item, or the option name to open that option";
export const TOP_N = 5;
export const SHOW_MORE = "Show more";
export const SHOW_LESS = "Show less";

export const SOURCE_LABEL = {
  manual: "Manual",
  latest_purchase: "Last purchase",
} as const;

/** Decimals for a unit price: 4 under £1, 2 under £20, else 0. */
export function priceDp(top: number): number {
  return top < 1 ? 4 : top < 20 ? 2 : 0;
}

export function unitDp(row: MarginRow): number {
  return priceDp(Math.max(row.current_unit_cost_gbp ?? 0, row.best_quote_gbp ?? 0));
}

export function stakeText(row: MarginRow): ReactNode {
  return row.no_current_cost
    ? <Chip tone="muted" title="No current cost or annual quantity for this item yet">No current cost</Chip>
    : money(row.at_stake_gbp ?? 0, 0);
}

export function savingCell(row: MarginRow): ReactNode {
  if (row.saving_per_unit_gbp == null) return "—";
  if (row.saving_per_unit_gbp > 0) return money(row.saving_per_unit_gbp, unitDp(row));
  return <Chip tone="muted" title="The cheapest costed option is not below today's cost.">None yet</Chip>;
}

export function bestCell(row: MarginRow): ReactNode {
  if (!row.best_option_id) return <Chip tone="muted">No costed option yet</Chip>;
  const age = row.best_quote_age_days ?? "—";
  return (
    <>
      <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
        <OptionLink optionId={row.best_option_id} itemId={row.item_id} from="margin_at_stake">
          {row.best_option_name ?? row.best_option_id}
        </OptionLink>
      </span>{" "}
      <b>{money(row.best_quote_gbp, unitDp(row))}</b>{" "}
      {row.best_quote_expired ? (
        <Chip tone="warn" title="Ingredient quotes must be refreshed every 90 days">Quote expired · {age} days</Chip>
      ) : (
        <Chip tone="muted">Quote {age} days old</Chip>
      )}
      {row.other_basis_n > 0 ? (
        <div className="text-xs text-ks-grey-1">
          {row.other_basis_n} quote line{row.other_basis_n === 1 ? "" : "s"} in another unit left out
        </div>
      ) : null}
    </>
  );
}

export const MARGIN_COLUMNS: ColumnDef<MarginRow, unknown>[] = [
  { id: "rank", header: "#", cell: ({ row }) => row.original.rank ?? "–" },
  {
    id: "item",
    header: "Item",
    cell: ({ row }) => (
      <>
        {/* The row opens the item too: stop here so one click is one navigation, not two. */}
        <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          <ItemLink itemId={row.original.item_id} from="margin_at_stake">{row.original.item_name}</ItemLink>
        </span>
        {row.original.product_lines ? <div className="text-xs text-ks-grey-1">{row.original.product_lines}</div> : null}
      </>
    ),
  },
  {
    id: "annual_volume",
    header: "Annual volume",
    meta: { align: "right" },
    cell: ({ row }) => row.original.annual_quantity == null
      ? "—"
      : `${row.original.annual_quantity.toLocaleString("en-GB")} ${row.original.unit_label ?? ""}`,
  },
  {
    id: "current_cost",
    header: "Current cost",
    meta: { align: "right" },
    cell: ({ row }) => (
      <>
        {money(row.original.current_unit_cost_gbp, unitDp(row.original))}
        {row.original.current_cost_source ? (
          <div className="text-xs text-ks-grey-1">{SOURCE_LABEL[row.original.current_cost_source]}</div>
        ) : null}
      </>
    ),
  },
  { id: "best", header: "Best costed option", cell: ({ row }) => bestCell(row.original) },
  { id: "saving", header: "Saving per unit", meta: { align: "right" }, cell: ({ row }) => savingCell(row.original) },
  { id: "at_stake", header: "£ at stake a year", meta: { align: "right" }, cell: ({ row }) => <b>{stakeText(row.original)}</b> },
  {
    id: "counts",
    header: () => <span title="Live options that are costed / scored / sampled">Costed / scored / sampled</span>,
    meta: { align: "right" },
    cell: ({ row }) => `${row.original.costed_n} / ${row.original.scored_n} / ${row.original.sampled_n}`,
  },
  {
    id: "last_movement",
    header: "Last movement",
    meta: { align: "right" },
    cell: ({ row }) => row.original.last_moved_days == null ? "—" : (
      <>{row.original.last_moved_days} days {row.original.last_moved_days >= 14 ? <Chip tone="warn">Stalled</Chip> : null}</>
    ),
  },
];

export function Tile({ label, value, title, sub }: { label: string; value: ReactNode; title?: string; sub?: ReactNode }) {
  return (
    <div className="rounded-md border border-ks-grey-3 px-3 py-2 text-xs" title={title}>
      {label}
      <span className="mt-0.5 block text-2xl font-semibold">{value}</span>
      {sub ? <span className="mt-0.5 block text-ks-grey-1">{sub}</span> : null}
    </div>
  );
}

export function Tiles({ data }: { data: MarginData }) {
  // The tile follows the same filters as the ranking (Stalled pins Dropped to hide itself).
  const stalled = useTabQuery<StalledData>({ ...tabFor("stalled"), accepts: ["funnel", "item", "phase", "option", "geo"] });
  // A dropped option is never stalled, so "Only dropped" (the ranking shows dropped rows only) counts none.
  const onlyDropped = readShared(useSearch({ strict: false }) as Record<string, unknown>).dropped === "only";
  const stalledN = onlyDropped
    ? 0
    : stalled.isSuccess && stalled.data.meta.health.status !== "failed"
      ? stalled.data.data?.stalled_n ?? "—"
      : "—";
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Tile label="At stake a year" value={money(data.total_at_stake_gbp, 0)} />
      <Tile label="Stalled options (14+ days)" value={<TabLink tabKey="stalled">{stalledN}</TabLink>} />
      <Tile label="Expired quotes" value={data.expired_quotes_n} />
      <Tile label="Next production run" value="—" title="Production runs are not linked yet" />
    </div>
  );
}

export function topRows<Row>(rows: Row[], open: boolean): Row[] {
  return open ? rows : rows.slice(0, TOP_N);
}

export function ShowMoreToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <div>
      <Button data-nonmutating type="button" variant="outline" size="sm" aria-expanded={open} onClick={onToggle}>
        {open ? SHOW_LESS : SHOW_MORE}
      </Button>
    </div>
  );
}

export function StakeBars({
  rows,
  open = false,
  onToggle = () => {},
}: {
  rows: MarginRow[];
  open?: boolean;
  onToggle?: () => void;
}) {
  const max = Math.max(1, ...rows.map((row) => row.at_stake_gbp ?? 0));
  return (
    <div className="flex flex-col gap-2">
      {topRows(rows, open).map((row) => (
        <div key={row.item_id} className="grid grid-cols-[120px_1fr_80px] items-center gap-3 text-sm md:grid-cols-[260px_1fr_110px]">
          <ItemLink itemId={row.item_id} from="margin_at_stake">{row.item_name}</ItemLink>
          <div className="h-3.5">
            <div className="h-3.5 rounded-[2px] bg-ks-purple" style={{ width: `${((row.at_stake_gbp ?? 0) / max) * 100}%` }} />
          </div>
          <div className="text-right tabular-nums">{stakeText(row)}</div>
        </div>
      ))}
      {rows.length > TOP_N ? <ShowMoreToggle open={open} onToggle={onToggle} /> : null}
    </div>
  );
}

export function MarginAtStakeBody({
  data,
  page,
  onPage,
  onOpen,
  barsOpen = false,
  tableOpen = false,
  onToggleBars = () => {},
  onToggleTable = () => {},
}: {
  data: MarginData;
  page: number;
  onPage: (next: number) => void;
  onOpen: (row: MarginRow) => void;
  barsOpen?: boolean;
  tableOpen?: boolean;
  onToggleBars?: () => void;
  onToggleTable?: () => void;
}) {
  if (data.ranked.length === 0) {
    return <Note><b>No margin items match these filters.</b> Clear the filters to see every margin item.</Note>;
  }
  const folded = data.ranked.length > TOP_N && !tableOpen;
  return (
    <div className="flex flex-col gap-4">
      <Tiles data={data} />
      <StakeBars rows={data.ranked} open={barsOpen} onToggle={onToggleBars} />
      <PagedTable<MarginRow>
        columns={MARGIN_COLUMNS}
        data={folded
          ? { items: data.ranked.slice(0, TOP_N), total: TOP_N, limit: TOP_N, offset: 0 }
          : pageSlice(data.ranked, page)}
        page={page}
        onPage={onPage}
        caption="Margin items ranked by money at stake"
        hidePager={folded}
        wrap
        onRowClick={onOpen}
        rowClassName={() => "cursor-pointer"}
      />
      {data.ranked.length > TOP_N ? <ShowMoreToggle open={tableOpen} onToggle={onToggleTable} /> : null}
      {data.not_ranked.length > 0 ? (
        <Footnote>
          Not margin items, so not ranked:{" "}
          {data.not_ranked.map((item, index) => (
            <span key={item.item_id}>
              <ItemLink itemId={item.item_id} from="margin_at_stake">{item.item_name}</ItemLink>
              {index < data.not_ranked.length - 1 ? ", " : null}
            </span>
          ))}
        </Footnote>
      ) : null}
      <Footnote>Last movement counts phase changes recorded since {dateGB(data.since)}.</Footnote>
    </div>
  );
}

export function MarginAtStakePage({ tab, areaName }: PageProps) {
  const [page, setPage] = usePage();
  const [barsOpen, setBarsOpen] = useState(false);
  const [tableOpen, setTableOpen] = useState(page > 1);
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { area?: string; module?: string };
  // A row opens its item (every row, quoted or not); the best-quote cell keeps its own link to that option.
  const open = (row: MarginRow) => {
    void navigate({
      to: "/app/$area/$module/$tab",
      params: { area: params.area ?? "sourcing", module: params.module ?? "sourcing_funnel", tab: "item_overview" },
      search: ((previous: Record<string, unknown>) => itemLinkSearch(previous, row.item_id, "margin_at_stake")) as never,
    } as never);
  };
  const toggleTable = () => {
    if (tableOpen && page !== 1) setPage(1);
    setTableOpen(!tableOpen);
  };
  return (
    <TabPage<MarginData>
      tab={tab}
      areaName={areaName}
      head={() => <PageHead title={tab.label} sub={MARGIN_SUB} />}
    >
      {(data) => (
        <MarginAtStakeBody
          data={data}
          page={page}
          onPage={setPage}
          onOpen={open}
          barsOpen={barsOpen}
          tableOpen={tableOpen}
          onToggleBars={() => setBarsOpen((value) => !value)}
          onToggleTable={toggleTable}
        />
      )}
    </TabPage>
  );
}
